import React, { useState } from "react";
import {
	Box,
	Button,
	FormControl,
	FormLabel,
	Input,
	Textarea,
	VStack,
	HStack,
	Tag,
	TagLabel,
	TagCloseButton,
	Text,
} from "@chakra-ui/react";
import { CreateArticleRequest } from "../../../models/KnowledgeBaseArticle";

interface ArticleCreatorProps {
	initialTags?: string[];
	onCreate: (request: CreateArticleRequest) => void;
	onCancel: () => void;
	isCreating: boolean;
}

/**
 * Component for creating new knowledge base articles.
 * Allows editors to create articles with title, content, tags, and image URLs.
 */
const ArticleCreator: React.FC<ArticleCreatorProps> = ({
	initialTags = [],
	onCreate,
	onCancel,
	isCreating,
}) => {
	const [title, setTitle] = useState("");
	const [content, setContent] = useState("");
	const [tags, setTags] = useState<string[]>(initialTags);
	const [imageUrls, setImageUrls] = useState<string[]>([]);
	const [newTag, setNewTag] = useState("");
	const [newImageUrl, setNewImageUrl] = useState("");

	const handleAddTag = () => {
		if (newTag.trim() && !tags.includes(newTag.trim())) {
			setTags([...tags, newTag.trim()]);
			setNewTag("");
		}
	};

	const handleRemoveTag = (tagToRemove: string) => {
		setTags(tags.filter((tag) => tag !== tagToRemove));
	};

	const handleAddImageUrl = () => {
		if (newImageUrl.trim()) {
			setImageUrls([...imageUrls, newImageUrl.trim()]);
			setNewImageUrl("");
		}
	};

	const handleRemoveImageUrl = (index: number) => {
		setImageUrls(imageUrls.filter((_, i) => i !== index));
	};

	const handleCreate = () => {
		const request: CreateArticleRequest = {
			title,
			content,
			tags,
		};

		if (imageUrls.length > 0) {
			request.imageUrls = imageUrls;
		}

		onCreate(request);
	};

	const canCreate = title.trim() !== "" && content.trim() !== "" && tags.length > 0;

	return (
		<Box>
			<VStack spacing={4} align="stretch">
				{/* Title */}
				<FormControl isRequired>
					<FormLabel>Titel</FormLabel>
					<Input
						value={title}
						onChange={(e) => setTitle(e.target.value)}
						placeholder="Artikeltitel"
					/>
				</FormControl>

				{/* Tags */}
				<FormControl isRequired>
					<FormLabel>Taggar</FormLabel>
					<HStack mb={2} flexWrap="wrap">
						{tags.map((tag) => (
							<Tag key={tag} size="md" colorScheme="blue" mb={2}>
								<TagLabel>{tag}</TagLabel>
								<TagCloseButton onClick={() => handleRemoveTag(tag)} />
							</Tag>
						))}
					</HStack>
					<HStack>
						<Input
							value={newTag}
							onChange={(e) => setNewTag(e.target.value)}
							placeholder="Lägg till tagg (t.ex. bokningstyp)"
							onKeyPress={(e) => e.key === "Enter" && handleAddTag()}
						/>
						<Button onClick={handleAddTag} size="sm">
							Lägg till
						</Button>
					</HStack>
				</FormControl>

				{/* Content */}
				<FormControl isRequired>
					<FormLabel>Innehåll (Markdown)</FormLabel>
					<Textarea
						value={content}
						onChange={(e) => setContent(e.target.value)}
						placeholder="Artikelinnehåll i markdown-format"
						rows={15}
						fontFamily="monospace"
					/>
					<Text fontSize="xs" color="gray.500" mt={1}>
						Stödjer: # Rubrik, ## Underrubrik, **fetstil**, *kursiv*, - punktlista
					</Text>
				</FormControl>

				{/* Image URLs - Hidden for now, will be enabled later */}
				{/* <FormControl>
					<FormLabel>Bild-URLer</FormLabel>
					{imageUrls.length > 0 && (
						<VStack align="stretch" mb={2} spacing={1}>
							{imageUrls.map((url, index) => (
								<HStack key={index}>
									<Text fontSize="sm" flex={1} noOfLines={1}>
										{url}
									</Text>
									<Button size="xs" colorScheme="red" onClick={() => handleRemoveImageUrl(index)}>
										Ta bort
									</Button>
								</HStack>
							))}
						</VStack>
					)}
					<HStack>
						<Input
							value={newImageUrl}
							onChange={(e) => setNewImageUrl(e.target.value)}
							placeholder="https://example.com/bild.png"
							onKeyPress={(e) => e.key === "Enter" && handleAddImageUrl()}
						/>
						<Button onClick={handleAddImageUrl} size="sm">
							Lägg till
						</Button>
					</HStack>
				</FormControl> */}

				{/* Action Buttons */}
				<HStack justify="flex-end" pt={4} borderTop="1px solid" borderColor="gray.200">
					<Button variant="ghost" onClick={onCancel} isDisabled={isCreating}>
						Avbryt
					</Button>
					<Button
						colorScheme="blue"
						onClick={handleCreate}
						isDisabled={!canCreate}
						isLoading={isCreating}
					>
						Skapa artikel
					</Button>
				</HStack>
			</VStack>
		</Box>
	);
};

export default ArticleCreator;
